/* eslint-disable no-param-reassign */
import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { kml } from '@tmcw/togeojson';
import { lineToPolygon } from '@turf/turf';
import { FeatureCollection, LineString } from 'geojson';
import jsZip from 'jszip';
import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useToastContext } from '@/providers/ToastProvider';

import FileUploadModal, {
  CustomValidation,
} from '../../components/common/file-upload-modal';
import usePrevious from '../../hooks/usePrevious';
import { FeatureType, IUploadFeature } from '../../models/features';
import { RootState } from '../../redux/reducers';
import { addFeature } from '../../redux/slices/featureSlice';
import { clearFileUploadModal } from '../../redux/slices/fileUploadModalSlice';
import { setUploadFile } from '../../redux/slices/settingsSlice';
import { createRandomString } from '../../utils/common';
import zoomToBounds from '../../utils/zoomToBounds';

const acceptedFileType = {
  'application/geo+json': ['.geojson', '.json'],
  'application/vnd.google-earth.kml+xml': ['.kml', '.xml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
  'text/plain': ['.wkt'],
};

const UploadFileModal: FunctionComponent = () => {
  const { mapInstance: map } = useMapboxContext();
  const { track } = useAnalytics();
  const { uploadFile } = useSelector((state: RootState) => state.settings);
  const { uploads } = useSelector((state: RootState) => state.features);
  const prevState: IUploadFeature[] = usePrevious(uploads);
  const { showErrorToast } = useToastContext();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!map || !uploads.length) return;

    if ((prevState && prevState.length) < uploads.length) {
      const lastUpload = uploads.at(-1);

      if (lastUpload) {
        zoomToBounds(map, lastUpload.geometry);
      }
    }

    dispatch(setUploadFile(false));
  }, [dispatch, map, prevState, uploads]);

  const handleLineStrings = (f, boundaries) => {
    try {
      const polygon = lineToPolygon(f as unknown as LineString);

      boundaries.push({
        ...f,
        geometry: polygon.geometry,
        id: createRandomString(10, 'number'),
        properties: {
          ...f.properties,
          type: FeatureType.UPLOAD,
        },
      } as IUploadFeature);
    } catch (err) {
      showErrorToast('Error converting LineString to Polygon');
    }
  };

  const assignProperties = (geojson: FeatureCollection) => {
    const boundaries: IUploadFeature[] = [];

    geojson.features.forEach((f) => {
      if (!f?.geometry) {
        return;
      }
      if (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') {
        boundaries.push({
          ...f,
          id: createRandomString(10, 'number'),
          properties: {
            ...f.properties,
            type: FeatureType.UPLOAD,
          },
        } as IUploadFeature);
      } else if (f.geometry.type === 'GeometryCollection') {
        f.geometry.geometries.forEach((g) => {
          if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
            boundaries.push({
              ...f,
              geometry: g,
              id: createRandomString(10, 'number'),
              properties: {
                ...f.properties,
                type: FeatureType.UPLOAD,
              },
            } as IUploadFeature);
          }

          if (g.type === 'LineString' || g.type === 'MultiLineString') {
            handleLineStrings(g, boundaries);
          }
        });
      } else if (
        f.geometry.type === 'MultiLineString' ||
        f.geometry.type === 'LineString'
      ) {
        handleLineStrings(f, boundaries);
      }
    });

    dispatch(
      addFeature({
        feature: boundaries,
      })
    );
  };

  const handleTracking = (file: File) => {
    track({
      data: {
        file_name: file.name,
        file_type: file.name.split('.').pop(),
      },
      event: TrackingEvent.FILE_UPLOADED,
    });
  };

  const changeHandler = async (file: File) => {
    const reader = new FileReader();
    // for geojson
    if (file.name.endsWith('.json') || file.name.endsWith('.geojson')) {
      reader.onload = (revt) => {
        const geojson = JSON.parse(
          revt.target?.result as any
        ) as FeatureCollection;

        assignProperties(geojson);
      };
      reader.readAsText(file);
      handleTracking(file);
    } else if (file.name.endsWith('.kml')) {
      reader.onload = (revt) => {
        const text = revt.target?.result;
        const xml = new DOMParser().parseFromString(
          text as any,
          'application/xml'
        );
        const geojson = kml(xml) as FeatureCollection;

        assignProperties(geojson);
      };
      reader.readAsText(file);
      handleTracking(file);
    } else if (file.name.endsWith('.kmz')) {
      jsZip.loadAsync(file).then((zip) => {
        zip.files['doc.kml'].async('string').then((fileData) => {
          const text = fileData;
          const xml = new DOMParser().parseFromString(
            text as any,
            'application/xml'
          );
          const geojson = kml(xml) as FeatureCollection;
          assignProperties(geojson);
          handleTracking(file);
        });
      });
    }
  };

  // Causing problems
  const customValidation: CustomValidation = (file: File) => {
    const type: string[] | undefined = acceptedFileType[file.type];
    const splitName = file.name.split('.');

    const fileExtension = splitName[splitName.length - 1];
    const fileExtensionWithDot = `.${fileExtension}`;

    if (
      !type ||
      !(type.includes(fileExtensionWithDot) || type.includes(fileExtension))
    ) {
      return {
        code: 'unsupported file type',
        message: 'This file type is not supported.',
      };
    }

    return null;
  };

  return (
    <FileUploadModal
      acceptedFileString=".geojson, .kml, .kmz"
      acceptedFileType={acceptedFileType}
      customValidation={customValidation}
      handleOnClose={() => {
        dispatch(setUploadFile(false));
        dispatch(clearFileUploadModal());
      }}
      isOpen={uploadFile}
      title="Upload File"
      onUpload={changeHandler}
    />
  );
};

export default UploadFileModal;
